import React, {useEffect, useState, useCallback, useMemo} from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  ScrollView,
  Image,
  ActivityIndicator,
  Platform,
} from 'react-native';
import type { V2Balance, V2HistoryRecord, V2TransactionType } from '../../types/v2';
import TabBar from '../../components/TabBar';
import SimpleHeader from '../../components/SimpleHeader';
import AccountBalancePanel from '../../components/AccountBalancePanel';
import AccountActionRow from '../../components/AccountActionRow';
import AccountCard from '../../components/UIComponents/AccountCard';
import { COLORS } from '../../theme/themeTokens';
import { v2GetMyHistory, v2GetMyBalances, v2GetTransactionTypes, v2TransactionTypeIconUrl } from '../../services/v2/api';
import { useAuth } from '../../contexts/AuthContext';
import { setSupportPrefill } from '../../tools/supportPrefill';
import { getTokenIconSource , getLocalTokenIcon} from '../../tools/getCurrencyIcon';
import TokenIcon from '../../components/UIComponents/TokenIcon';
import { formatSignedAmount, signedAmountStyle } from '../../utils/transactionSign';
import { isConfirmedStatus } from '../../utils/transactionStatus';
import { formatTokenAmount } from '../../utils/amountFormat';
import { compareByNetwork } from '../../utils/networkOrder';

const parseAmount = (s: string | null | undefined): number => {
  const n = parseFloat(s ?? '');
  return isNaN(n) ? 0 : n;
};

/** Format a number with space as thousands separator: 1 234 567.89 */
const withSpaces = (num: number, decimals = 2): string => {
  const [int, dec] = Math.abs(num).toFixed(decimals).split('.');
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + '.' + dec;
};

const formatBalance = (amount: string): string => {
  const num = parseFloat(amount);
  if (isNaN(num)) return '0.00';
  return withSpaces(num);
};

/** "14.04.2026" */
const formatDate = (dateString: string): string => {
  const d = new Date(dateString);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

/** Group records by date key (dd.MM.yyyy) preserving order, sorted desc */
const groupByDate = (
  items: V2HistoryRecord[],
): {date: string; items: V2HistoryRecord[]}[] => {
  // Sort descending by date
  const sorted = [...items].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
  const map = new Map<string, V2HistoryRecord[]>();
  for (const rec of sorted) {
    const key = formatDate(rec.date);
    const group = map.get(key);
    if (group) {
      group.push(rec);
    } else {
      map.set(key, [rec]);
    }
  }
  return Array.from(map.entries()).map(([date, items]) => ({date, items}));
};

/** "Deposit" → "Deposit" */
const capitalize = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

// formatAmount + sign visuals moved to src/utils/transactionSign so the
// "catalog-only" rule (no numeric fallback) is enforced by a single
// source across history lists. Import below pulls the shared helpers.

/**
 * Collapse a transaction-type name to a stable category key — same drift
 * mitigation used in TransactionHistoryScreen so a row whose backend name is
 * "Withdrawal" still finds the catalog entry "Withdraw".
 */
const normalizeTypeName = (name: string | null | undefined): string => {
  const lower = (name || '').toLowerCase().trim();
  if (lower.includes('withdr')) return 'withdraw';
  // "depos" covers legacy "Deposit"; "inflow" is the canonical backend
  // name after the v1_2_transaction_types rename (migration 00065).
  if (lower.includes('depos') || lower.includes('inflow')) return 'deposit';
  return lower;
};

// ── Transaction Item (compact) ───────────────────────────────────────────────

const TransactionItem: React.FC<{
  item: V2HistoryRecord;
  typeIconUri: string | null;
  amountSign: V2TransactionType['amount_sign'] | null;
}> = ({ item, typeIconUri, amountSign }) => {
  const formattedAmount = formatSignedAmount(item.amount, amountSign);
  const { color: amountColor } = signedAmountStyle(amountSign);
  // BC-shim: matches both legacy `completed` и новый `confirmed`. TODO:
  // убрать legacy после полного деплоя backend 1.4 на прод.
  const isPending = item.transaction_status_name.toLowerCase() === 'pending';
  const isCompleted =
    item.transaction_status_name.toLowerCase() === 'completed' ||
    item.transaction_status_name.toLowerCase() === 'success' ||
    item.transaction_status_name.toLowerCase() === 'confirmed';

  return (
    <View style={localStyles.txRow}>
      {/* Left: Icon — admin-managed per transaction type */}
      <View style={localStyles.txIconContainer}>
        {typeIconUri ? (
          <Image
            source={{ uri: typeIconUri }}
            style={localStyles.txIcon}
            resizeMode="contain"
          />
        ) : (
          <View style={[localStyles.txIcon, localStyles.txIconPlaceholder]}>
            <Text style={localStyles.txIconPlaceholderText}>
              {capitalize(item.transaction_type_name).charAt(0) || '?'}
            </Text>
          </View>
        )}
      </View>

      {/* Middle: type + status + date */}
      <View style={localStyles.txInfo}>
        <Text
          style={localStyles.txTypeName}
          numberOfLines={1}>
          {capitalize(item.transaction_type_name)}
        </Text>
      </View>

      {/* Right: amount */}
      <View style={localStyles.txAmountBlock}>
        <Text
          style={[localStyles.txAmount, { color: amountColor }]}
          numberOfLines={1}>
          {formattedAmount} {item.token_symbol || item.currency}
        </Text>
      </View>
    </View>
  );
};

interface BalanceDetailScreenProps {
  balance: V2Balance;
  onBack: () => void;
  onNavigate: (screen: string) => void;
  onDeposit?: () => void;
  onWithdraw?: () => void;
  onTopUp?: () => void;
  /** Opens Operations on the Exchange tab with this account as the Sell side. */
  onExchange?: () => void;
  onHistoryPress?: (record: V2HistoryRecord) => void;
}

const BalanceDetailScreen: React.FC<BalanceDetailScreenProps> = ({
  balance,
  onBack,
  onNavigate,
  onDeposit,
  onWithdraw,
  onTopUp,
  onExchange,
  onHistoryPress,
}) => {
  const { isAuthenticated } = useAuth();
  // Status-bar inset for Android — see CardScreen for the same value;
  // iOS gets 0 because App-level SafeAreaView handles the notch.
  const statusBarHeight = Platform.OS === 'android' ? 28 : 0;
  const currency = balance.token_symbol || 'USDT';
  const [history, setHistory] = useState<V2HistoryRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  // "Sibling" balances — rows with the same token_symbol scoped to current
  // accounts. One row per network, so the user sees the split visually.
  // Starts with just the clicked balance so the first paint isn't empty.
  const [siblings, setSiblings] = useState<V2Balance[]>([balance]);
  const [siblingsLoading, setSiblingsLoading] = useState(true);
  // Transaction types catalog — drives the per-row icon and sign prefix on
  // history entries. Same lookup pattern as TransactionHistoryScreen.
  const [transactionTypes, setTransactionTypes] = useState<V2TransactionType[]>([]);

  // Two catalog maps with different semantics:
  //
  //  - `signByExactName`: type.name → amount_sign. Each type carries
  //    its OWN sign, so the lookup MUST be exact. Collapsing via
  //    normalizeTypeName would overwrite (e.g. transfer_deposit_to_current
  //    is positive but transfer_current_to_deposit is negative — both
  //    normalize to "deposit" and the last one wins).
  //
  //  - `iconByCategory`: normalized name → icon. Multiple types share
  //    an icon family ("Withdraw" + "Withdraw to card"), so collapsing
  //    is fine — and the legacy code intentionally falls back to the
  //    first non-null icon in the family.
  //
  // Render path uses `signByExactName.get(record.transaction_type_name)`
  // for the sign and falls back to `iconByCategory.get(normalize(name))`
  // for the icon when the exact-name lookup misses (name drift between
  // record snapshot and catalog).
  const signByExactName = useMemo(() => {
    const m = new Map<string, V2TransactionType['amount_sign']>();
    for (const t of transactionTypes) m.set(t.name, t.amount_sign);
    return m;
  }, [transactionTypes]);
  // Exact-name icon lookup — preferred over the category fallback so
  // each type that has its own icon configured in OneAdmin renders
  // exactly that icon (e.g. transfer_deposit_to_current must NOT borrow
  // open_deposit_account's icon just because they share a normalized
  // category key).
  const iconByExactName = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const t of transactionTypes) m.set(t.name, v2TransactionTypeIconUrl(t));
    return m;
  }, [transactionTypes]);
  // Category-level icon — only used as a fallback when the exact type
  // has no icon of its own. Legacy types ("Withdraw" + "Withdraw to
  // card") still share a single icon via this map.
  const iconByCategory = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const t of transactionTypes) {
      const key = normalizeTypeName(t.name);
      const iconUri = v2TransactionTypeIconUrl(t);
      if (!m.has(key) || (m.get(key) == null && iconUri != null)) {
        m.set(key, iconUri);
      }
    }
    return m;
  }, [transactionTypes]);

  const fetchHistory = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setHistoryLoading(true);
      const res = await v2GetMyHistory({ token_symbol: currency });
      // Filter to the screen's own account scope — without this, a
      // deposit-side record (close_deposit_account, inflow_deposit_account,
      // open_deposit_account, outflow_deposit_account) would leak into
      // the current-account view because token_symbol is the same. The
      // record's account_type_code reflects WHERE the row sits
      // (the underlying balance's account type), which is the authoritative
      // discriminator regardless of legacy data drift.
      // Two layered filters:
      //  1. account_type_code — scope by screen owner (current vs
      //     investment vs card); see comment block above.
      //  2. status === confirmed — pending / in_process / cancelled
      //     applications stay invisible on the history list. Uses the
      //     BC-aware helper so legacy `completed` / `success` rows
      //     also count as "confirmed" during the dictionary cutover.
      const filtered = (res.records || []).filter(
        (r) =>
          r.account_type_code === balance.account_type_code &&
          isConfirmedStatus(r.transaction_status_name),
      );
      const byDateDesc = [...filtered].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );
      // Show the full history for this token on the Current account
      // screen — capping at 5 hid older transactions and meant the
      // ScrollView had no overflow to scroll through. The dedicated
      // History tab still exists for filtering/searching across all
      // tokens; here we just show every confirmed record for the token
      // the user opened.
      setHistory(byDateDesc);
    } catch (err) {
      console.warn('[BalanceDetailScreen] Failed to fetch history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [isAuthenticated, currency]);

  const fetchSiblings = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setSiblingsLoading(true);
      const res = await v2GetMyBalances();
      const all = res.balances ?? [];
      // Scope: same token, same account-type as the clicked balance. Without
      // the account-type guard, cards/deposits with the same token would
      // leak into the current-account view.
      //
      // Drop synthetic virtual aggregates (provider_name='virtual'): the
      // backend emits one virtual DTO per (account, token) in addition to
      // the real per-network rows so legacy clients keep working. The total
      // is already shown in AccountBalancePanel above; per-network split
      // should only list real on-chain destinations, otherwise we'd
      // duplicate the total as an extra tile.
      const matching = all.filter(
        (b) =>
          b.token_symbol === balance.token_symbol &&
          (b.account_type_code ?? null) === (balance.account_type_code ?? null) &&
          b.provider_name !== 'virtual',
      );
      if (matching.length > 0) {
        setSiblings(matching);
      }
    } catch (err) {
      console.warn('[BalanceDetailScreen] Failed to fetch siblings:', err);
    } finally {
      setSiblingsLoading(false);
    }
  }, [isAuthenticated, balance.token_symbol, balance.account_type_code]);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchHistory(), fetchSiblings()]);
    })();
  }, [fetchHistory, fetchSiblings]);

  // One-shot fetch of transaction types — non-blocking; per-row icon falls
  // back to the type-letter placeholder until the catalog lands.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await v2GetTransactionTypes();
        if (!cancelled) {
          setTransactionTypes(res.transaction_types ?? []);
        }
      } catch (err) {
        console.warn('[BalanceDetailScreen] Failed to fetch transaction types:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

      {/* Uniform 20 dp vertical rhythm — every block on this screen
          contributes exactly 20 dp to its gap with the next one. The
          paddingBottom on the header wrapper compensates for the
          per-platform paddingBottom inside SimpleHeader so the gap
          from title to amount is 20 on both iOS and Android. */}
      <View style={{
        paddingTop: statusBarHeight,
        paddingBottom: Platform.OS === 'android' ? 12 : 0,
        backgroundColor: COLORS.primaryNavy,
      }}>
        <SimpleHeader
          title="Current account"
          onBack={onBack}
          backgroundVariant="primaryNavy"
        />
      </View>

      <AccountBalancePanel
        amount={formatBalance(balance.amount)}
        currency={currency}
        available={
          parseAmount(balance.locked_amount ?? '0') > 0
            ? `${formatBalance(balance.available_amount ?? balance.amount)} ${currency}`
            : undefined
        }
        style={{ paddingTop: 0, paddingBottom: 20 }}
      />

      <AccountActionRow
        onNavy
        onInflow={onDeposit}
        onWithdraw={onWithdraw}
        onTopUp={onTopUp}
        onExchange={onExchange}
        style={{ paddingTop: 0, paddingBottom: 0 }}
      />

      {/* Networks frame — rendered OUTSIDE the ScrollView so the tiles
          sit on the navy background, directly under the action row.
          Mirrors how AccountCards sit on the navy strip on the Dashboard
          + AllAssets screens. */}
      {(() => {
        if (siblingsLoading) {
          return (
            <View style={localStyles.networksFrame}>
              <View style={localStyles.loadingContainer}>
                <ActivityIndicator size="small" color={COLORS.white} />
              </View>
            </View>
          );
        }
        // Dedup by natural key (network + wallet address) — id can collide
        // when the balances API returns the same row twice, but network +
        // wallet uniquely identifies a real on-chain destination.
        const naturalKey = (b: V2Balance) =>
          `${b.network_symbol || 'no-net'}|${b.wallet_address || b.wallet_id || b.id}`;
        // Every network the token lives on, funded or not. A zero row used
        // to be dropped, so a client holding USDT on Tron alone saw one
        // line and no sign that the same token can arrive over Ethereum —
        // which is the thing they need to know before asking anyone to
        // send them any. Ordered TRON, Ethereum, then the rest, the same
        // order the Withdraw and Top-up pickers use.
        const seen = new Set<string>();
        const unique = siblings.filter((b) => {
          const k = naturalKey(b);
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        unique.sort((a, b) => compareByNetwork(
          { networkSymbol: a.network_symbol || '' },
          { networkSymbol: b.network_symbol || '' },
        ));
        // Fallback: no per-network rows at all (siblings never loaded, or
        // the token has none) — show the tapped tile rather than nothing.
        const uniqueVisible = unique.length > 0 ? unique : [balance];
        // Renders one row inside the merged card. Layout mirrors
        // AccountCard's visible structure (icon + name/subtitle column
        // + value на правом краю) — но без individual chrome'а (border,
        // bg, radius), потому что outer mergedCard несёт это сам.
        // Контент (icon/name/subtitle/value) идентичен тому что
        // отрисовывал бы AccountCard с теми же data — пользовательский
        // запрос: "ничего по содержимому не меняем".
        const renderRow = (b: V2Balance) => {
          let title = '';
          if (b.token_symbol && b.network_symbol) {
            title = `${b.token_symbol} - ${b.network_symbol}`;
          } else {
            title = b.token_symbol || b.token_name || '';
          }
          // The whole balance, with the held part under it — the same
          // shape the Dashboard tile uses, so tapping through does not
          // change the number the client just looked at.
          const value = parseAmount(b.amount);
          const lockedValue = parseAmount(b.locked_amount ?? '0');
          return (
            <View style={localStyles.mergedRow}>
              <TokenIcon
                symbol={b.token_symbol}
                iconUrl={b.icon_url}
                style={localStyles.mergedRowIcon}/>
              <View style={localStyles.mergedRowLeft}>
                <Text style={localStyles.mergedRowName} numberOfLines={1}>
                  {title}
                </Text>
                {b.network_name ? (
                  <Text style={localStyles.mergedRowSubtitle} numberOfLines={1}>
                    {b.network_name}
                  </Text>
                ) : null}
              </View>
              <View style={localStyles.mergedRowRight}>
                <Text style={localStyles.mergedRowValue} numberOfLines={1}>
                  {formatTokenAmount(value, b.token_decimals ?? 2)}
                </Text>
                {lockedValue > 0 ? (
                  <Text style={localStyles.mergedRowSubtitle} numberOfLines={1}>
                    {`\u{1F512} ${formatTokenAmount(lockedValue, b.token_decimals ?? 2)}`}
                  </Text>
                ) : null}
              </View>
            </View>
          );
        };
        return (
          <View style={localStyles.networksFrame}>
            {/* Single network → один AccountCard как раньше. Несколько
                сетей того же token'а — merge'им в одну плашку с
                горизонтальным gray-разделителем (marginHorizontal:2
                чтобы линия отступала от краёв chrome'а). */}
            {uniqueVisible.length === 1 ? (
              <AccountCard
                key={naturalKey(uniqueVisible[0])}
                style={localStyles.networkCardWrapper}
                data={{
                  icon: getTokenIconSource(uniqueVisible[0].token_symbol, uniqueVisible[0].icon_url),
                  iconFallback: getLocalTokenIcon(uniqueVisible[0].token_symbol),
                  name: uniqueVisible[0].token_symbol && uniqueVisible[0].network_symbol
                    ? `${uniqueVisible[0].token_symbol} - ${uniqueVisible[0].network_symbol}`
                    : uniqueVisible[0].token_symbol || uniqueVisible[0].token_name || '',
                  valueUSDT: parseAmount(uniqueVisible[0].value_usd ?? uniqueVisible[0].amount),
                  value: parseAmount(uniqueVisible[0].amount),
                  locked: parseAmount(uniqueVisible[0].locked_amount ?? '0'),
                  changePercent: parseFloat(uniqueVisible[0].change ?? '0'),
                  decimals: uniqueVisible[0].token_decimals,
                }}
                subtitle={uniqueVisible[0].network_name || undefined}
                showValueUSDT={false}
                showChangePercent={false}
                disableShake
                spacious
              />
            ) : (
              <View style={localStyles.mergedCard}>
                {uniqueVisible.map((b, idx) => (
                  <React.Fragment key={naturalKey(b)}>
                    {renderRow(b)}
                    {idx < uniqueVisible.length - 1 && (
                      <View style={localStyles.mergedSeparator} />
                    )}
                  </React.Fragment>
                ))}
              </View>
            )}
          </View>
        );
      })()}

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}>
        {/* History Frame */}
        <View style={localStyles.historyFrame}>
          <Text style={localStyles.historyTitle}>History</Text>

          {historyLoading ? (
            <View style={localStyles.loadingContainer}>
              <ActivityIndicator size="small" color={COLORS.primaryNavy} />
            </View>
          ) : history.length === 0 ? (
            <View style={localStyles.emptyContainer}>
              <Text style={localStyles.emptyText}>No transactions yet</Text>
            </View>
          ) : (
            (() => {
              const grouped = groupByDate(history);
              return grouped.map((group) => (
                <View key={group.date}>
                  <Text style={localStyles.dateHeader}>{group.date}</Text>
                  {group.items.map((item) => {
                    const name = item.transaction_type_name;
                    // amount_sign — strictly exact-name lookup so each
                    // type keeps its own catalog sign (close vs inflow
                    // share normalized key but have opposite signs).
                    const amountSign = signByExactName.get(name) ?? null;
                    // Icon — own icon first, then category fallback for
                    // legacy types whose icon was only registered on a
                    // related "family" row.
                    const iconUri =
                      iconByExactName.get(name) ??
                      iconByCategory.get(normalizeTypeName(name)) ??
                      null;
                    return (
                      <TouchableOpacity
                        key={item.id}
                        activeOpacity={0.7}
                        onPress={() => onHistoryPress?.(item)}>
                        <TransactionItem
                          item={item}
                          typeIconUri={iconUri}
                          amountSign={amountSign}
                        />
                      </TouchableOpacity>
                    );
                  })}
                </View>
              ));
            })()
          )}
        </View>
      </ScrollView>

      <TabBar activeTab="dashboard" onNavigate={onNavigate} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  content: {
    flex: 1,
    // Rounded top corners + pureLight fill so the History scroll area
    // matches the look used on WithdrawScreen / OpenDepositScreen /
    // NotificationsScreen — gray sheet "rising" out of the navy strip.
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
  },
  contentContainer: {
    flexGrow: 1,
    paddingTop: 0,
    // TabBar is rendered with position:'absolute', bottom:0 so it
    // overlaps the bottom of the ScrollView's frame without shrinking
    // it via flex. Without this padding, the last history rows sit
    // permanently under the TabBar AND the ScrollView "thinks" its
    // content fits inside the visible area — so scroll never engages.
    // 120 matches the convention used on every other TabBar-screen
    // (TransactionHistoryScreen, DepositAccountScreen, Card/* …).
    paddingBottom: 120,
  },
});

// ── Local Styles (History Frame) ─────────────────────────────────────────────

const localStyles = StyleSheet.create({
  networksFrame: {
    paddingHorizontal: 12,
    // 20 dp top keeps the ActionRow → first tile gap uniform with
    // the rest of the screen. Bottom is 0 — the pureLight history
    // sheet starts right against the last tile, and the 20 dp gap
    // to the "History" title is owned by historyTitle.marginTop.
    paddingTop: 20,
    paddingBottom: 0,
    // Sits on the navy strip directly under the action row — same
    // visual layer as the Dashboard / AllAssets account tiles. Was
    // pureLight (gray) when rendered inside the ScrollView body.
    backgroundColor: COLORS.primaryNavy,
  },
  networkCardWrapper: {
    // 20 dp between sibling account tiles, matching the screen's
    // global vertical-rhythm step.
    marginBottom: 20,
  },
  // ── Merged-tile стили для случая когда у юзера есть тот же токен на
  // нескольких сетях (например USDT/TRC20 + USDT/ERC20). Chrome повторяет
  // AccountCard (white bg + borderMuted 1px borer + radius:8) чтобы
  // визуально читалось как одна плашка вместо N разных. Между row'ами —
  // gray линия с marginHorizontal:2 (отступы по 2px от краёв chrome'а).
  mergedCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 20,
  },
  mergedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 14,
    height: 74,
  },
  mergedRowIcon: {
    width: 32,
    height: 32,
    marginRight: 10,
  },
  mergedRowLeft: {
    flex: 1,
    justifyContent: 'center',
  },
  mergedRowName: {
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  mergedRowSubtitle: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 8,
  },
  mergedRowRight: {
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  mergedRowValue: {
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  mergedSeparator: {
    height: 1,
    backgroundColor: COLORS.borderMuted,
    marginHorizontal: 2,
  },
  historyFrame: {
    // No `flex: 1` here — when the frame's child is clamped to viewport
    // height, the parent ScrollView has nothing to scroll. Without flex,
    // the frame grows naturally with the list and the ScrollView starts
    // scrolling as soon as content exceeds the visible area. The parent
    // ScrollView's contentContainerStyle ({flexGrow: 1}) still fills the
    // empty-state case below the History title.
    backgroundColor: COLORS.pureLight,
    padding: 0,
  },
  historyTitle: {
    fontFamily: 'Inter',
    fontSize: 20,
    fontWeight: '600',
    lineHeight: 22,
    color: COLORS.primaryNavy,
    marginTop: 20,
    marginLeft: 22,
    // 20 dp gap to the first history row, matching the screen's
    // global vertical rhythm.
    marginBottom: 20,
  },
  loadingContainer: {
    paddingVertical: 0,
    paddingLeft: 10,
    alignItems: 'center',
  },
  emptyContainer: {
    paddingVertical: 0,
    paddingLeft: 10,
    alignItems: 'center',
  },
  emptyText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.blueSteel,
  },
  dateHeader: {
    fontFamily: 'Inter',
    fontSize: 15,
    fontWeight: '500',
    lineHeight: 18,
    color: COLORS.blueSteel,
    marginTop: 12,
    marginLeft: 22,
    marginBottom: 8,
  },
  txRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.pureLight,
    borderRadius: 10,
    padding: 12,
    marginLeft: 10,
    marginBottom: 4,
  },
  txIconContainer: {
    marginRight: 12,
  },
  // Square icons matching TransactionHistoryScreen (36×36, no border-radius).
  // Previously this tile rendered the same icons as a 32-circle (borderRadius:16),
  // which looked off versus the History screen where the same admin-managed
  // type icons are square.
  txIcon: {
    width: 36,
    height: 36,
  },
  txIconPlaceholder: {
    backgroundColor: COLORS.softGrey,
    justifyContent: 'center',
    alignItems: 'center',
  },
  txIconPlaceholderText: {
    fontSize: 18,
    fontWeight: '600',
    color: COLORS.slate,
  },
  txInfo: {
    flex: 1,
    justifyContent: 'center',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statusIcon: {
    width: 12,
    height: 12,
  },
  txTypeName: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.primaryNavy,
    marginBottom: 2,
  },
  statusBadgeText: {
    fontFamily: 'Inter',
    fontSize: 13,
    lineHeight: 15,
    fontWeight: '400',
    color: COLORS.blueSteel,
  },
  txAmountBlock: {
    alignItems: 'flex-end',
    marginLeft: 8,
  },
  txAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
  },
  txAmountPositive: {
    color: COLORS.mint,
  },
  txAmountNegative: {
    color: COLORS.primaryNavy,
  },
  txUsd: {
    fontSize: 14,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
});

export default BalanceDetailScreen;
